// drawPleatedScreen.js
// Draws a pleated (accordion) insect screen door: front view + top view in ONE svg,
// using the same coordinate conventions as the window / patio door drawings:
//   - (initX, initY) is the inner origin, the outer frame starts at (initX - frameWidth, initY - frameWidth)
//   - sizes are inches * SCALE
//   - dimension lines come from displaySizes(), same look as windows / patio doors
//
// Screen types (doorData.frameData.selectedDoor.id):
//   1 = Single Leaf, by frameData.orientation:
//       'Left to Right' (default): mesh anchored on the left jamb, handle bar closes on the right jamb
//       'Right to Left':           mirrored, handle bar closes on the left jamb
//   2 = Double Leaf: meshes anchored on both jambs, handle bars meet in the middle

import { SCALE } from '../constants';
import { displaySizes } from './displaySizes';
import { formatToFractional } from '../controls/conversions';

export const SCREEN_FRAME_WIDTH = 5;   // screen profiles are slimmer than window frames
const BAR_WIDTH = 7;                   // handle (slider) bar
const DIMENSION_SPACE = 30;            // room under the front view for the width dimension
const TOP_VIEW_CAPTION_GAP = 14;
const TOP_VIEW_DEPTH = 12;             // jamb / bar depth in the top view
const TOP_VIEW_HEIGHT = TOP_VIEW_CAPTION_GAP + TOP_VIEW_DEPTH + 20;

// Height (px, unscaled) the drawing needs below the front view's outer frame.
export const pleatedScreenExtraHeight = () => DIMENSION_SPACE + TOP_VIEW_HEIGHT;

export function getScreenFrameColor(doorData) {
  if (doorData?.frameData?.screenFrameColor === 'Gentek' && doorData?.frameData?.gentekColor) {
    return doorData.frameData.gentekColor;
  }
  return '#FFFFFF';
}

// Vertical pleat folds: alternating dark/light lines read as an accordion mesh.
function definePleatPattern(svg, patternId) {
  const defs = svg.append('defs');
  const pattern = defs.append('pattern')
    .attr('id', patternId)
    .attr('patternUnits', 'userSpaceOnUse')
    .attr('width', 6)
    .attr('height', 6);

  pattern.append('rect')
    .attr('width', 6)
    .attr('height', 6)
    .attr('fill', '#F4F6F7');
  pattern.append('line')
    .attr('x1', 0.5).attr('y1', 0).attr('x2', 0.5).attr('y2', 6)
    .attr('stroke', '#9AA3A8')
    .attr('stroke-width', 0.8);
  pattern.append('line')
    .attr('x1', 3.5).attr('y1', 0).attr('x2', 3.5).attr('y2', 6)
    .attr('stroke', '#D5DADD')
    .attr('stroke-width', 0.6);
}

function drawMesh(svg, x, y, w, h, patternId) {
  if (w <= 0 || h <= 0) return;
  svg.append('rect')
    .attr('x', x).attr('y', y)
    .attr('width', w).attr('height', h)
    .attr('fill', `url(#${patternId})`)
    .attr('stroke', '#666')
    .attr('stroke-width', 0.5);
}

function drawHandleBar(svg, x, y, h, frameColor) {
  svg.append('rect')
    .attr('x', x).attr('y', y)
    .attr('width', BAR_WIDTH).attr('height', h)
    .attr('fill', frameColor)
    .attr('stroke', 'black')
    .attr('stroke-width', 1);

  // Grip at mid-height
  svg.append('rect')
    .attr('x', x + 1.5).attr('y', y + h / 2 - 9)
    .attr('width', BAR_WIDTH - 3).attr('height', 18)
    .attr('rx', 1.5)
    .attr('fill', '#555');
}

function drawMagnet(svg, x, y, w, h) {
  svg.append('rect')
    .attr('x', x).attr('y', y)
    .attr('width', w).attr('height', h)
    .attr('fill', '#222');
}

function drawCaption(svg, x, y, text) {
  svg.append('text')
    .attr('x', x).attr('y', y)
    .attr('font-size', 12)
    .attr('font-weight', 'bold')
    .attr('fill', '#333')
    .text(text);
}

function drawZigzag(svg, x1, x2, yMid, amplitude = 4, step = 5) {
  if (x2 - x1 < step) return;
  const n = Math.max(2, Math.round((x2 - x1) / step));
  const dx = (x2 - x1) / n;
  const points = [];
  for (let i = 0; i <= n; i++) {
    points.push(`${x1 + i * dx},${yMid + (i % 2 === 0 ? -amplitude : amplitude)}`);
  }
  svg.append('polyline')
    .attr('points', points.join(' '))
    .attr('fill', 'none')
    .attr('stroke', '#777')
    .attr('stroke-width', 1.2);
}

function drawProfile(svg, x, yTop, w, frameColor) {
  svg.append('rect')
    .attr('x', x).attr('y', yTop)
    .attr('width', w).attr('height', TOP_VIEW_DEPTH)
    .attr('fill', frameColor)
    .attr('stroke', 'black')
    .attr('stroke-width', 1);
}

/**
 * @param svg        d3 selection (usually the zoomed <g>)
 * @param doorData   pleated screen doorData
 * @param initX/Y    inner origin (same meaning as for windows / patio doors)
 * @param patternId  unique id for the mesh pattern (several drawings can be on one page)
 */
export function drawPleatedScreen(svg, doorData, initX, initY, patternId = 'pleatedMeshPattern') {
  const fw = SCREEN_FRAME_WIDTH;
  const widthIn = parseFloat(doorData?.frameData?.frameWidth) || 0;
  const heightIn = parseFloat(doorData?.frameData?.frameHeight) || 0;
  if (widthIn < 10 || heightIn < 10) return;  // avoid drawing while the user types the first digit

  const isDouble = doorData?.frameData?.selectedDoor?.id === 2;
  const closesLeft = !isDouble && doorData?.frameData?.orientation === 'Right to Left';
  const magnetLatch = doorData?.frameData?.magnetLatch === 'Yes';
  const frameColor = getScreenFrameColor(doorData);

  const W = widthIn * SCALE;
  const H = heightIn * SCALE;
  const ox = initX - fw;
  const oy = initY - fw;

  definePleatPattern(svg, patternId);

  // ===== FRONT VIEW =====
  drawCaption(svg, ox, oy - 10, 'Front View');

  // Outer frame (head, sill, jambs)
  svg.append('rect')
    .attr('x', ox).attr('y', oy)
    .attr('width', W).attr('height', H)
    .attr('fill', frameColor)
    .attr('stroke', 'black')
    .attr('stroke-width', 1);

  const innerX = ox + fw;
  const innerY = oy + fw;
  const innerW = W - 2 * fw;
  const innerH = H - 2 * fw;

  if (isDouble) {
    const cx = ox + W / 2;
    drawMesh(svg, innerX, innerY, cx - BAR_WIDTH - innerX, innerH, patternId);
    drawMesh(svg, cx + BAR_WIDTH, innerY, innerX + innerW - (cx + BAR_WIDTH), innerH, patternId);
    drawHandleBar(svg, cx - BAR_WIDTH, innerY, innerH, frameColor);
    drawHandleBar(svg, cx, innerY, innerH, frameColor);
    if (magnetLatch) {
      drawMagnet(svg, cx - 1.5, innerY + innerH / 2 - 40, 3, 22);
    }
  } else if (closesLeft) {
    const barX = innerX;
    drawMesh(svg, barX + BAR_WIDTH, innerY, innerX + innerW - (barX + BAR_WIDTH), innerH, patternId);
    drawHandleBar(svg, barX, innerY, innerH, frameColor);
    if (magnetLatch) {
      drawMagnet(svg, ox + 1, innerY + innerH / 2 - 40, fw - 2, 22);
    }
  } else {
    const barX = innerX + innerW - BAR_WIDTH;
    drawMesh(svg, innerX, innerY, barX - innerX, innerH, patternId);
    drawHandleBar(svg, barX, innerY, innerH, frameColor);
    if (magnetLatch) {
      drawMagnet(svg, ox + W - fw + 1, innerY + innerH / 2 - 40, fw - 2, 22);
    }
  }

  const w_label = doorData?.frameData?.frameWidthLabel || formatToFractional(widthIn);
  const h_label = doorData?.frameData?.frameHeightLabel || formatToFractional(heightIn);
  displaySizes(svg, initX, initY, W, H, w_label, h_label, 0, '', fw);

  // ===== TOP VIEW =====
  const captionY = oy + H + DIMENSION_SPACE + TOP_VIEW_CAPTION_GAP;
  drawCaption(svg, ox, captionY, 'Top View');

  const ty = captionY + 8;               // top of the profiles
  const yMid = ty + TOP_VIEW_DEPTH / 2;

  drawProfile(svg, ox, ty, fw, frameColor);              // left jamb
  drawProfile(svg, ox + W - fw, ty, fw, frameColor);     // right jamb

  if (isDouble) {
    const cx = ox + W / 2;
    drawZigzag(svg, ox + fw, cx - BAR_WIDTH, yMid);
    drawZigzag(svg, cx + BAR_WIDTH, ox + W - fw, yMid);
    drawProfile(svg, cx - BAR_WIDTH, ty, BAR_WIDTH, frameColor);
    drawProfile(svg, cx, ty, BAR_WIDTH, frameColor);
  } else if (closesLeft) {
    const barX = ox + fw;
    drawZigzag(svg, barX + BAR_WIDTH, ox + W - fw, yMid);
    drawProfile(svg, barX, ty, BAR_WIDTH, frameColor);
  } else {
    const barX = ox + W - fw - BAR_WIDTH;
    drawZigzag(svg, ox + fw, barX, yMid);
    drawProfile(svg, barX, ty, BAR_WIDTH, frameColor);
  }
}
